import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api, errText } from "@/lib/auth";
import { Sigil } from "@/components/temple/Sigil";

const LABEL = { approved: "Approved — shown anonymously as “An Initiate”.", rejected: "Rejected — kept private.", pending: "Awaiting your decision." };
const base = "inline-flex items-center justify-center gap-3 rounded-full px-7 py-4 font-tt-mono text-[12px] uppercase tracking-[0.2em] transition-colors disabled:opacity-60";

export default function ReviewReflection() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const wanted = params.get("action") === "reject" ? "rejected" : "approved";
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/reflections/review", { params: { token } })
      .then(({ data }) => alive && setInfo(data))
      .catch((e) => alive && setError(errText(e.response?.data?.detail)));
    return () => {
      alive = false;
    };
  }, [token]);

  const decide = async (status) => {
    setBusy(true);
    try {
      const { data } = await api.post("/reflections/review", { token, status });
      setInfo(data);
    } catch (e) {
      setError(errText(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const other = wanted === "approved" ? "rejected" : "approved";
  const btn = (s, primary) => (
    <button key={s} data-testid={`review-${s === "approved" ? "approve" : "reject"}`} disabled={busy} onClick={() => decide(s)} className={`${base} ${primary ? (s === "approved" ? "bg-[#D9B47A] text-[#0A0706] hover:bg-[#F2E8DF]" : "bg-[#C2414F] text-[#F2E8DF] hover:bg-[#D9B47A] hover:text-[#0A0706]") : "border border-[#F2E8DF]/30 hover:border-[#D9B47A] hover:text-[#D9B47A]"}`}>
      {busy && primary && <Loader2 className="w-4 h-4 animate-spin" />}{s === "approved" ? "Approve" : "Reject"}
    </button>
  );

  return (
    <main data-testid="review-reflection-page" className="min-h-screen flex items-center justify-center px-5 pt-24 pb-16">
      <div className="w-full max-w-xl">
        <Sigil animate className="w-14 h-14" width={2.5} />
        <p className="mt-8 font-tt-mono text-[12px] uppercase tracking-[0.35em] text-[#D9B47A]">Review a reflection</p>
        {error && <p data-testid="review-error" className="mt-6 text-lg text-[#E8838E]">{error}</p>}
        {!info && !error && <Loader2 className="mt-8 w-6 h-6 animate-spin text-[#D9B47A]" />}
        {info && (
          <>
            <blockquote data-testid="review-text" className="mt-6 font-display text-3xl sm:text-4xl leading-snug">“{info.text}”</blockquote>
            <p data-testid="review-status" data-status={info.status} className="mt-6 text-[#CFC2B6]">{LABEL[info.status] ?? ""}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              {info.status === "pending" ? [btn(wanted, true), btn(other, false)] : btn(info.status === "approved" ? "rejected" : "approved", false)}
            </div>
          </>
        )}
        <Link to="/inbox?tab=reflections" className="block mt-10 text-sm text-[#A8998C] hover:text-[#D9B47A]">Open the Inbox</Link>
      </div>
    </main>
  );
}
